import { useState } from 'react';
import { Plus, Play, Pause, Pencil, Clock, ChevronRight, ChevronDown, MoreHorizontal, ArrowLeft, Check } from 'lucide-react';

// ── Pulses ───────────────────────────────────────────────────────────────────
// Work that runs on a schedule. Two surfaces: a list of pulses, and one pulse
// in detail (its brief, its configuration, its run history).
//
// The list leads with the two things you actually check — is it healthy, and
// when does it run next — with a strip of recent outcomes as the quiet proof.
// ─────────────────────────────────────────────────────────────────────────────

type RunStatus = 'completed' | 'failed' | 'running';
interface Run {
  at: string;
  finished: string;
  status: RunStatus;
  duration: string;
  summary: string;
}
interface Pulse {
  id: string;
  name: string;
  kind: string;
  cadence: string;
  next: string;
  nextAbsolute: string;
  destination: string;
  group: string;
  enabled: boolean;
  runCount: number;
  history: RunStatus[];
  prompt: {
    intro: string;
    access: string[];
    steps: string[];
  };
  runs: Run[];
}
const heartbeatRuns = (from: number): Run[] => Array.from({
  length: 12
}, (_, i) => {
  const h = from - Math.floor(i / 2);
  const m = i % 2 === 0 ? '30' : '00';
  return {
    at: `Aug 28, ${String(h).padStart(2, '0')}:${m}`,
    finished: `Aug 28, ${String(h).padStart(2, '0')}:${m}`,
    status: 'completed' as RunStatus,
    duration: `${32 + i * 7 % 30}s`,
    summary: 'No new bookings. 1 call starting inside 4 hours — reminder already sent.'
  };
});
export const PULSES: Pulse[] = [{
  id: 'calendly',
  name: 'New demo booking alerts',
  kind: 'Heartbeat',
  cadence: 'Every 30 minutes',
  next: 'in 14m',
  nextAbsolute: 'Aug 28, 2026, 12:00',
  destination: '#marketing',
  group: 'Marketing',
  enabled: true,
  runCount: 44,
  history: ['completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed'],
  prompt: {
    intro: "Check Brightwave's Calendly for newly booked demo calls and calls starting within four hours, then post the right update to the marketing team. Keep a small state file so nothing is announced twice.",
    access: ['Calendly — Brightwave demo calendar via a connected credential.', 'Google Calendar — Sarah Chen and the sales team.', 'Slack channel `#marketing` via `send_slack_message`.', 'State file — track `demo-booking-alerts`, file `state.json`. If missing, start fresh.'],
    steps: ['Read `state.json` for `last_run_at` and the list of already-reminded event URIs.', 'Fetch active bookings: `GET /scheduled_events?user=<user_uri>&status=active`.', 'Any event created after `last_run_at` is new — fetch its invitees and post “New demo booked”.', 'Any event starting inside 4 hours and not yet reminded — post a reminder and record it.', 'Write `state.json` back with the new timestamp and reminder list.']
  },
  runs: heartbeatRuns(11)
}, {
  id: 'campaign',
  name: 'Daily campaign performance brief',
  kind: 'Daily digest',
  cadence: 'Daily at 09:00 UTC',
  next: 'in 23h',
  nextAbsolute: 'Aug 29, 2026, 09:00',
  destination: '#growth',
  group: 'Marketing',
  enabled: true,
  runCount: 31,
  history: ['completed', 'completed', 'failed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'completed'],
  prompt: {
    intro: 'Summarise every live campaign each morning — spend, pipeline created, and anything that moved more than 15% overnight — and post one digest to #growth.',
    access: ['HubSpot — vault credential `hubspot-brightwave`, campaigns and deals endpoints.', 'Google Sheets — spend tracker `Q3 Paid Spend`.', 'Slack channel `#growth` via `send_slack_message`.'],
    steps: ['Pull yesterday’s spend per campaign from the sheet.', 'Pull attributed pipeline per campaign from HubSpot.', 'Flag anything that moved more than 15% against the 7-day average.', 'Post one digest, leading with the flagged campaigns.']
  },
  runs: [{
    at: 'Aug 28, 09:00',
    finished: 'Aug 28, 09:01',
    status: 'completed',
    duration: '1m 12s',
    summary: 'Two campaigns flagged — Search Brand up 22%, Retargeting down 31%.'
  }, {
    at: 'Aug 27, 09:00',
    finished: 'Aug 27, 09:01',
    status: 'completed',
    duration: '58s',
    summary: 'No campaign moved more than 15%. Spend steady at $4.2k/day.'
  }, {
    at: 'Aug 26, 09:00',
    finished: 'Aug 26, 09:00',
    status: 'failed',
    duration: '14s',
    summary: 'HubSpot returned 401 — credential needs reauthorising.'
  }, {
    at: 'Aug 25, 09:00',
    finished: 'Aug 25, 09:01',
    status: 'completed',
    duration: '1m 4s',
    summary: 'Retargeting up 18% after the new creative went live.'
  }]
}, {
  id: 'ambassador',
  name: 'Customer advocacy daily recap',
  kind: 'Daily digest',
  cadence: 'Daily at 09:00 UTC',
  next: 'in 23h',
  nextAbsolute: 'Aug 29, 2026, 09:00',
  destination: 'Direct message',
  group: 'Operations',
  enabled: true,
  runCount: 26,
  history: ['completed', 'completed', 'completed', 'completed', 'completed', 'completed', 'running', 'completed', 'completed', 'completed', 'completed', 'completed'],
  prompt: {
    intro: 'Each morning, recap what the ambassador programme did yesterday — new applications, content published, and referrals converted — and send it as a direct message.',
    access: ['Airtable — `Customer Advocates` base, applications and content tables.', 'Stripe — referral conversions via Brightwave’s connected account.'],
    steps: ['Count new applications and flag any waiting more than 48 hours.', 'List content published, with links.', 'Total referral conversions and revenue.', 'Send one message, applications first.']
  },
  runs: [{
    at: 'Aug 28, 09:00',
    finished: 'Aug 28, 09:00',
    status: 'completed',
    duration: '41s',
    summary: '6 new applications (2 waiting 48h+), 3 posts published, 4 referrals converted.'
  }, {
    at: 'Aug 27, 09:00',
    finished: 'Aug 27, 09:00',
    status: 'completed',
    duration: '38s',
    summary: '3 new applications, 1 post published, 2 referrals converted.'
  }]
}, {
  id: 'renewal',
  name: 'Renewal risk sweep',
  kind: 'Weekly report',
  cadence: 'Fridays at 16:00 UTC',
  next: 'Paused',
  nextAbsolute: '—',
  destination: '#revenue',
  group: 'Revenue',
  enabled: false,
  runCount: 9,
  history: ['completed', 'completed', 'completed', 'failed', 'completed', 'completed', 'completed', 'completed', 'completed'],
  prompt: {
    intro: 'Every Friday, find accounts renewing inside 60 days with no contact logged in the last 21 days, and post them to #revenue with the owner tagged.',
    access: ['Salesforce — accounts and activity history.', 'Slack channel `#revenue`.'],
    steps: ['Query accounts with a renewal date inside 60 days.', 'Filter to those with no logged activity in 21 days.', 'Sort by contract value, tag the owner, post the list.']
  },
  runs: [{
    at: 'Aug 22, 16:00',
    finished: 'Aug 22, 16:01',
    status: 'completed',
    duration: '1m 33s',
    summary: 'Four accounts flagged, $284k combined. Acme and Northwind both quiet 28 days.'
  }]
}];

// A run outcome as a single tick. Twelve of them read as a health strip
// without needing a chart or a number.
function HistoryStrip({
  history
}: {
  history: RunStatus[];
}) {
  return <span className="flex items-center gap-[3px]" aria-hidden="true" data-id="element-3863" data-mp="1ss3ieinhpzuc">
      {history.slice(0, 12).reverse().map((s, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${s === 'failed' ? 'bg-[#DC2626]/70' : s === 'running' ? 'bg-[#EA580C]' : 'bg-emerald-500/35'}`} data-id="element-3864" data-mp="22o6gndf81ec0" data-mpi={i} />)}
    </span>;
}
function StatusPill({
  status
}: {
  status: RunStatus;
}) {
  const map = {
    completed: 'bg-app-subtle text-[var(--app-success)]',
    failed: 'bg-app-subtle text-[var(--app-danger)]',
    running: 'bg-app-accent-tint text-app-accent'
  } as const;
  const label = {
    completed: 'Completed',
    failed: 'Failed',
    running: 'Running'
  } as const;
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${map[status]}`} data-id="element-3865" data-mp="2j7b87vfpa8al">
      {label[status]}
    </span>;
}

// Turns `code` spans in the brief into real chips, so the prompt reads as a
// document rather than a wall of backticks.
function Prose({
  text
}: {
  text: string;
}) {
  return <span dangerouslySetInnerHTML={{
    __html: text.replace(/`([^`]+)`/g, '<code class="rounded bg-app-subtle px-1.5 py-0.5 font-mono text-[12.5px] text-app">$1</code>')
  }} data-id="element-3866" data-mp="2nzvvsf80jp5o" />;
}
export function PulsesView() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'runs'>('overview');
  const [filter, setFilter] = useState<'enabled' | 'paused' | 'all'>('enabled');
  const [filterOpen, setFilterOpen] = useState(false);
  const pulse = PULSES.find(p => p.id === openId);

  // ── Detail ─────────────────────────────────────────────────────────────────
  if (pulse) {
    return <div className="mx-auto max-w-4xl px-6 pb-16 pt-16 md:px-8 md:pt-14" data-id="element-3867" data-mp="12g8otq015l8g">
        <button type="button" onClick={() => setOpenId(null)} className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-app-muted transition-colors duration-200 ease-out hover:text-app" data-id="element-3868" data-mp="12id3n2zgw4ni">
          <ArrowLeft size={13} data-id="element-3869" data-mp="thn880e4cgab" />
          Pulses
        </button>

        <header className="flex flex-wrap items-start justify-between gap-4 pb-7" data-id="element-3870" data-mp="yfggr02w9mdr">
          <div data-id="element-3871" data-mp="1m5aqh8rx2uts">
            <h1 className="text-[21px] font-semibold tracking-[-0.015em] text-app" data-id="element-3872" data-mp="1m4rkmqfjjww4">
              {pulse.name}
            </h1>
            <p className="mt-1.5 text-[14.5px] text-app-muted" data-id="element-3873" data-mp="1q7xqhwyqrm22">
              {pulse.kind} · {pulse.cadence}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-3874" data-mp="1hcq06h87yzv0">
            <span className="hidden items-center gap-1.5 pr-1 text-[13px] text-app-muted sm:flex" data-id="element-3875" data-mp="1hbev8e31fm2b">
              <Clock size={13} className="text-app-subtle" data-id="element-3876" data-mp="2510wr86vuddh" />
              Next {pulse.next}
            </span>
            <button type="button" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-app bg-app-surface px-3 text-[13.5px] font-semibold text-app transition-colors duration-200 ease-out hover:border-app-strong hover:bg-app-hover" data-id="element-3877" data-mp="29vnavj4cmk19">
              <Pencil size={13} data-id="element-3878" data-mp="10w7xn10itr0j" />
              Edit
            </button>
            <button type="button" className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-app-accent px-3.5 text-[13.5px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)]" data-id="element-3879" data-mp="14y1uey7liavf">
              <Play size={13} data-id="element-3880" data-mp="vyoilyw01a7j" />
              Run now
            </button>
          </div>
        </header>

        <div className="mb-6 flex items-center gap-5 border-b border-app-divider" data-id="element-3881" data-mp="haleyw5fialw">
          {([['overview', 'Overview', null], ['runs', 'Runs', pulse.runCount]] as const).map(([id, label, count]) => <button key={id} type="button" onClick={() => setTab(id)} className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-[14px] font-semibold transition-colors duration-200 ease-out ${tab === id ? 'border-app-accent text-app' : 'border-transparent text-app-muted hover:text-app'}`} data-id="element-3882" data-mp="89nayduzq33b">
                {label}
                {count !== null && <span className="text-[12.5px] font-medium text-app-subtle" data-id="element-3883" data-mp="2mbmva8faoka">
                    {count}
                  </span>}
              </button>)}
        </div>

        {tab === 'overview' ? <div className="grid gap-6 lg:grid-cols-[1fr_260px]" data-id="element-3884" data-mp="3usgu57jin7h6">
            {/* The brief, as a readable document sitting directly on the canvas. */}
            <div data-id="element-3885" data-mp="7kd5xv5umnvr">
              <div className="flex items-center justify-between gap-3" data-id="element-3886" data-mp="7kv818f6zwbl">
                <h2 className="text-[16px] font-semibold text-app" data-id="element-3887" data-mp="makg7nlpd0ld">
                  Brief
                </h2>
                <span className="rounded-full bg-app-subtle px-2.5 py-0.5 text-[12px] font-medium text-app-muted" data-id="element-3888" data-mp="hciusw0t0p6q">
                  {pulse.kind}
                </span>
              </div>
              <p className="mt-3 text-[15px] leading-relaxed text-app" data-id="element-3889" data-mp="qcxig218qgde">
                {pulse.prompt.intro}
              </p>

              <h3 className="mt-7 border-t border-app-divider pt-4 text-[12.5px] font-semibold text-app-muted" data-id="element-3890" data-mp="3k5p2frt0sojo">
                What it can reach
              </h3>
              <ul className="mt-2.5 space-y-2" data-id="element-3891" data-mp="3t4aer57wf7c0">
                {pulse.prompt.access.map(a => <li key={a} className="flex gap-2.5 text-[14px] leading-relaxed text-app-muted" data-id="element-3892" data-mp="1pmnrtl4zhkl">
                    <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-[var(--app-border-strong)]" data-id="element-3893" data-mp="6p7tqnyq4q6t" />
                    <span data-id="element-3894" data-mp="bnj3kyp0r0p1">
                      <Prose text={a} data-id="element-3895" data-mp="bmr7znvf92yr" />
                    </span>
                  </li>)}
              </ul>

              <h3 className="mt-7 border-t border-app-divider pt-4 text-[12.5px] font-semibold text-app-muted" data-id="element-3896" data-mp="gnnbi0bv2n3n">
                What it does each run
              </h3>
              <ol className="mt-2.5 space-y-2.5" data-id="element-3897" data-mp="2ifa8qjmcje8k">
                {pulse.prompt.steps.map((s, i) => <li key={s} className="flex gap-3 text-[14px] leading-relaxed text-app-muted" data-id="element-3898" data-mp="2mfkbzoir7qjq" data-mpi={i}>
                    <span className="mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-app-subtle text-[11px] font-semibold text-app-muted" data-id="element-3899" data-mp="2ex4d97cbv1i3">
                      {i + 1}
                    </span>
                    <span data-id="element-3900" data-mp="257ku00kpd5mp">
                      <Prose text={s} data-id="element-3901" data-mp="254ovaa116cx7" />
                    </span>
                  </li>)}
              </ol>
            </div>

            <aside className="rounded-xl bg-app-surface p-5 shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-3902" data-mp="2xvglmqvf07cm">
              <h2 className="text-[12.5px] font-semibold text-app-muted" data-id="element-3903" data-mp="2swccg08hq9ma">Configuration</h2>
              <dl className="mt-3 space-y-3" data-id="element-3904" data-mp="2ouquw1l0h75r">
                {[['Status', pulse.enabled ? 'Enabled' : 'Paused'], ['Schedule', pulse.cadence], ['Next run', pulse.enabled ? `${pulse.next} · ${pulse.nextAbsolute}` : '—'], ['Posts to', pulse.destination], ['Group', pulse.group], ['Runs so far', String(pulse.runCount)]].map(([k, v]) => <div key={k} data-id="element-3905" data-mp="18fzdejpelkb8">
                    <dt className="text-[12.5px] text-app-subtle" data-id="element-3906" data-mp="18fpnew4czd5j">{k}</dt>
                    <dd className="mt-0.5 text-[14px] font-medium text-app" data-id="element-3907" data-mp="2wcov47kl9ppz">
                      {v}
                    </dd>
                  </div>)}
              </dl>
              <button type="button" className="mt-5 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-app bg-app-surface text-[13px] font-semibold text-app transition-colors duration-200 ease-out hover:border-app-strong hover:bg-app-hover" data-id="element-3908" data-mp="315rl65q5qh1f">
                {pulse.enabled ? <>
                    <Pause size={13} data-id="element-3909" data-mp="3k2sxkbm1jjfe" />
                    Pause pulse
                  </> : <>
                    <Play size={13} data-id="element-3910" data-mp="36304waqyet4s" />
                    Resume pulse
                  </>}
              </button>
            </aside>
          </div> : <ul className="overflow-hidden rounded-xl bg-app-surface shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-3911" data-mp="3b11s97vexem2">
            {pulse.runs.map(r => <li key={r.at} className="flex items-start gap-4 border-b border-app-divider px-5 py-4 last:border-b-0" data-id="element-3912" data-mp="1umjr0lv1skk1">
                <span className="min-w-0 flex-1" data-id="element-3913" data-mp="1zkl9p5okahuz">
                  <span className="flex flex-wrap items-center gap-2" data-id="element-3914" data-mp="1r4m7mz9bv93p">
                    <span className="text-[14.5px] font-semibold text-app" data-id="element-3915" data-mp="1n4vamdt7jwqq">
                      {r.at}
                    </span>
                    <StatusPill status={r.status} data-id="element-3916" data-mp="212sapk6vela5" />
                  </span>
                  <span className="mt-1 block text-[13.5px] leading-relaxed text-app-muted" data-id="element-3917" data-mp="1w4qr8y5j8jy5">
                    {r.summary}
                  </span>
                </span>
                <span className="shrink-0 text-right" data-id="element-3918" data-mp="13gct8ieg1nml">
                  <span className="block text-[12.5px] text-app-subtle" data-id="element-3919" data-mp="13hxn05sxp2je">
                    {r.duration}
                  </span>
                </span>
              </li>)}
          </ul>}
      </div>;
  }

  // ── List ───────────────────────────────────────────────────────────────────
  const shown = PULSES.filter(p => filter === 'all' || (filter === 'enabled' ? p.enabled : !p.enabled));
  return <div className="mx-auto max-w-4xl px-6 pb-16 pt-16 md:px-8 md:pt-14" data-id="element-3920" data-mp="kp23i4kgfgwa">
      <header className="flex flex-wrap items-end justify-between gap-4 pb-7" data-id="element-3921" data-mp="2ksw7ngpsl0ck">
        <div data-id="element-3922" data-mp="2pqxpjybhetmg">
          <h1 className="text-[22px] font-semibold tracking-[-0.015em] text-app" data-id="element-3923" data-mp="2pqo1whdwsyjx">
            Pulses
          </h1>
          <p className="mt-1.5 text-[14.5px] leading-6 text-app-muted" data-id="element-3924" data-mp="2uiz09ft04cad">
            Work that runs on a schedule without being asked.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-3925" data-mp="21t8kl2bq12c5">
          <div className="relative" data-id="element-3926" data-mp="21uju7irul4b6">
            <button type="button" onClick={() => setFilterOpen(o => !o)} aria-expanded={filterOpen} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-app bg-app-surface px-3 text-[13.5px] font-medium text-app transition-colors duration-200 ease-out hover:border-app-strong hover:bg-app-hover" data-id="element-3927" data-mp="21vl6fqklnwuh">
              {filter === 'enabled' ? 'Enabled' : filter === 'paused' ? 'Paused' : 'All'}
              <ChevronDown size={14} className={`text-app-subtle transition-transform duration-200 ${filterOpen ? 'rotate-180' : ''}`} data-id="element-3928" data-mp="3r86k8jdz2dc6" />
            </button>
            {filterOpen && <>
                <div className="fixed inset-0 z-30" onClick={() => setFilterOpen(false)} data-id="element-3929" data-mp="58k5yja291iv" />
                <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[150px] overflow-hidden rounded-xl border border-app bg-app-surface py-1 shadow-[0_20px_50px_-20px_rgba(20,20,24,0.28)]" data-id="element-3930" data-mp="9z0o5fybrnk">
                  {(['enabled', 'paused', 'all'] as const).map(f => <button key={f} type="button" onClick={() => {
                setFilter(f);
                setFilterOpen(false);
              }} className="flex w-full items-center justify-between px-3 py-2.5 text-left text-[13.5px] font-medium text-app transition-colors duration-200 ease-out hover:bg-app-hover" data-id="element-3931" data-mp="3imneizutkxhb">
                      {f === 'enabled' ? 'Enabled' : f === 'paused' ? 'Paused' : 'All'}
                      {filter === f && <Check size={13} strokeWidth={3} className="text-app-accent" data-id="element-3932" data-mp="16va1ll0fnvwd" />}
                    </button>)}
                </div>
              </>}
          </div>
          <button type="button" className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-app-accent px-3.5 text-[13.5px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)]" data-id="element-3933" data-mp="1m2zgsxsgjmos">
            <Plus size={14} strokeWidth={2.5} data-id="element-3934" data-mp="1ccwn0vdii4pe" />
            New pulse
          </button>
        </div>
      </header>

      {shown.length === 0 ? <div className="rounded-xl bg-app-surface px-6 py-12 text-center shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-3935" data-mp="12f8jbfxxhmv3">
          <p className="text-[14.5px] font-semibold text-app" data-id="element-3936" data-mp="17euvrn5xmz2u">
            Nothing {filter === 'paused' ? 'paused' : 'enabled'}
          </p>
          <p className="mx-auto mt-1.5 max-w-[42ch] text-[13.5px] leading-relaxed text-app-muted" data-id="element-3937" data-mp="tg4z28n0z1f0">
            A pulse is a job Giga runs on a schedule — a morning digest, a
            weekly sweep, a check every half hour.
          </p>
        </div> : <ul className="overflow-hidden rounded-xl bg-app-surface shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-3938" data-mp="2sreh8xfglyhx">
          {shown.map(p => <li key={p.id} data-id="element-3939" data-mp="32utlnl80gr3h">
              <button type="button" onClick={() => {
          setOpenId(p.id);
          setTab('overview');
        }} className="flex w-full items-center gap-4 border-b border-app-divider px-5 py-4 text-left transition-colors duration-200 ease-out last:border-b-0 hover:bg-app-hover" data-id="element-3940" data-mp="32w4jl4erz4mo">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${p.enabled ? 'bg-emerald-500' : 'bg-[var(--app-border-strong)]'}`} data-id="element-3941" data-mp="36vn80a5t1xu9" />
                <span className="min-w-0 flex-1" data-id="element-3942" data-mp="3gmzzv1rlso0a">
                  <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-3943" data-mp="3gm83vpu3go9h">
                    {p.name}
                  </span>
                  <span className="mt-1 block truncate text-[12.5px] text-app-muted" data-id="element-3944" data-mp="3ukg5wc5zmfri">
                    {p.cadence} · {p.destination}
                  </span>
                </span>
                <span className="hidden shrink-0 sm:block" data-id="element-3945" data-mp="20aubwfm1xg4z">
                  <HistoryStrip history={p.history} data-id="element-3946" data-mp="1w87sn33g53je" />
                </span>
                <span className="hidden w-[76px] shrink-0 text-right text-[12.5px] text-app-muted sm:block" data-id="element-3947" data-mp="13kxx2zk4h615">
                  {p.enabled ? p.next : 'Paused'}
                </span>
                <ChevronRight size={15} className="shrink-0 text-app-subtle" data-id="element-3948" data-mp="1db11rx0lw8fb" />
              </button>
            </li>)}
        </ul>}

      <p className="mt-3 px-1 text-[12px] text-app-subtle" data-id="element-3949" data-mp="18bo3mg4abexf">
        Each strip shows the last twelve runs, oldest on the left.
      </p>
    </div>;
}